import { appIconAt } from './appIcon'
import { keepDocumentPinned } from './viewportPin'

/**
 * Portal OS as an app — an icon you tap, not a bookmark (24 Sep 2026).
 *
 *   • `installAppIcons()` — the picture. Safe for anybody, staff or client,
 *     and called at APP BOOT from App.tsx.
 *   • `installStandaloneApp()` — opening without browser chrome, the notch
 *     insets and the web app manifest. Staff only, called from PortalApp.
 *
 * **Why the split, and why boot.** Everything used to run from PortalApp,
 * which only mounts once you are signed in. But /admin redirects a signed-out
 * visitor to /staff, and a browser reads a page's icons when the page loads —
 * so Safari never saw ours. What it saw instead was the one icon Magic
 * Patterns ships in the host page: a 32-pixel favicon, enlarged to Dock size.
 * That was the blurry icon, through four rebuilds of artwork that was never
 * the problem. Confirmed by reading the live page's `<head>` signed out,
 * rather than reasoned about. Four plausible explanations cost more than one
 * look at the thing itself.
 *
 * **Standalone stays staff-only.** `apple-mobile-web-app-capable` removes the
 * browser's back button for anyone who installs the page, which is fine here
 * (the wordmark is the way home) and would be a trap on a client review link.
 */

const INK = '#0b0b0a'

/** 256 rather than Apple's documented 180: a Retina Dock draws around 256. */
const APPLE_ICON = 256

/** Chrome wants 192 and 512 before it will offer Install. */
const MANIFEST_ICONS = [192, 512]

function meta(name: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.name = name
    document.head.appendChild(tag)
  }
  tag.content = content
}

/**
 * Replaces every link of this `rel`, rather than editing the first one. The
 * host page ships its own `rel="icon"`, and patching that tag's href left the
 * browser free to prefer a cached copy of the file it first saw.
 */
function setLink(rel: string, href: string, sizes?: string) {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((node) => node.remove())
  const tag = document.createElement('link')
  tag.setAttribute('rel', rel)
  tag.setAttribute('type', 'image/png')
  if (sizes) tag.setAttribute('sizes', sizes)
  tag.setAttribute('href', href)
  document.head.appendChild(tag)
}

/** The icon, everywhere, from the first paint. Safe on the client side. */
export async function installAppIcons(): Promise<void> {
  if (typeof document === 'undefined') return

  const icon = await appIconAt(APPLE_ICON)
  if (!icon) return

  const size = `${APPLE_ICON}x${APPLE_ICON}`
  document.head.querySelectorAll('link[rel="shortcut icon"]').forEach((node) => node.remove())
  setLink('icon', icon, size)
  setLink('apple-touch-icon', icon, size)
  /* Older Safari looks for the precomposed name first. */
  setLink('apple-touch-icon-precomposed', icon, size)

  meta('theme-color', INK)
  meta('application-name', 'Portal OS')
}

/**
 * Lets the page reach into the notch and the home-indicator strip — and,
 * crucially, lets it know how deep they are.
 *
 * Without `viewport-fit=cover` iOS reports every `env(safe-area-inset-*)` as
 * 0px, so the `.pt-safe` and `.pb-safe` rules in index.css silently do
 * nothing and the installed app puts its wordmark in the clock and its tab
 * labels under the home indicator.
 *
 * It has to be patched in from here because the host page's own viewport meta
 * does not carry it and this app has no index.html to edit.
 *
 * **AND THAT PATCH IS WHY `keepDocumentPinned()` EXISTS.** Growing the
 * viewport after layout leaves iOS parked at a negative document scroll —
 * exactly the status-bar height, and permanently. It cost a day and three
 * wrong fixes; the whole account is in utils/viewportPin.ts. Read it before
 * removing that call.
 */
function coverViewport() {
  const tag = document.head.querySelector<HTMLMetaElement>('meta[name="viewport"]')
  if (!tag) {
    meta('viewport', 'width=device-width, initial-scale=1, viewport-fit=cover')
  } else if (!/viewport-fit\s*=/.test(tag.content)) {
    tag.content = `${tag.content.replace(/,\s*$/, '')}, viewport-fit=cover`
  }
  keepDocumentPinned()
}

/**
 * The web app manifest — what Chrome, Edge, Brave and Android read, and what
 * makes "Install Portal OS" appear in the address bar.
 *
 * Served from a blob URL because this app has no index.html to put a file in.
 * A blob URL inherits the document's origin, which is what the installability
 * check wants, and it is never revoked — the browser may re-read the manifest
 * at any point while the page is open.
 *
 * **`any maskable`, both.** Android masks every icon to the launcher's shape,
 * cropping roughly a quarter off each edge. An `any`-only icon is shrunk onto
 * a white card; a wide one has its sides cut, which is how Shilpa's install
 * came out reading "Por".
 */
function installManifest(icons: { size: number; src: string }[]) {
  if (typeof URL === 'undefined' || !URL.createObjectURL) return

  const start = `${window.location.origin}${window.location.pathname}`
  const manifest = {
    name: 'Portal OS',
    short_name: 'Portal OS',
    description: 'Portal Productions studio admin',
    start_url: start,
    scope: start,
    display: 'standalone',
    orientation: 'portrait',
    background_color: INK,
    theme_color: INK,
    icons: icons.map((icon) => ({
      src: icon.src,
      sizes: `${icon.size}x${icon.size}`,
      type: 'image/png',
      purpose: 'any maskable',
    })),
  }

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (existing) existing.remove()
  const link = document.createElement('link')
  link.setAttribute('rel', 'manifest')
  link.setAttribute('href', URL.createObjectURL(blob))
  document.head.appendChild(link)
}

/**
 * Opening without browser chrome. Staff side only — see the note at the top.
 * Safe to call more than once.
 */
export async function installStandaloneApp(): Promise<void> {
  if (typeof document === 'undefined') return

  coverViewport()
  meta('apple-mobile-web-app-capable', 'yes')
  meta('mobile-web-app-capable', 'yes')
  meta('apple-mobile-web-app-title', 'Portal OS')
  /* The ink page runs under the status bar rather than stopping at a white
     strip above it. */
  meta('apple-mobile-web-app-status-bar-style', 'black-translucent')

  const drawn = await Promise.all(
    MANIFEST_ICONS.map(async (size) => ({ size, src: await appIconAt(size) })),
  )
  const icons = drawn.filter((icon) => Boolean(icon.src))
  if (icons.length === MANIFEST_ICONS.length) installManifest(icons)
}
